// admin/src/pages/GatewayMonitoring/components/UnhealthySummaryBanner.js
'use strict';

import React from 'react';
import { Paper, Box, Typography, Stack } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * Top summary banner computed in the UI from the gateway list:
 * summarizes offline gateways, degraded gateways, and gateways dropping events.
 */
export default function UnhealthySummaryBanner({ gateways = [], mode = 'light' }) {
  if (!gateways || gateways.length === 0) return null;

  const offline = gateways.filter((g) => String(g.status).toLowerCase() === 'offline');
  const degraded = gateways.filter((g) => String(g.status).toLowerCase() === 'degraded');
  const dropping = gateways.filter((g) => Number(g.droppedEvents || 0) > 0);

  const hasIssues = offline.length > 0 || degraded.length > 0 || dropping.length > 0;

  if (!hasIssues) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: mode === 'dark' ? 'rgba(0, 168, 143, 0.12)' : '#E6F7F4',
          borderColor: mode === 'dark' ? '#00A88F' : '#A3E3D8',
        }}
      >
        <CheckCircleOutlineIcon sx={{ color: mode === 'dark' ? '#2FD3B8' : '#00A88F', fontSize: 24 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: mode === 'dark' ? '#2FD3B8' : '#007A68' }}>
            All Gateways Healthy
          </Typography>
          <Typography variant="body2" sx={{ color: mode === 'dark' ? '#B2DFDB' : '#00584B' }}>
            {gateways.length} active gateway{gateways.length > 1 ? 's' : ''} connected and reporting health normally.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const isCritical = offline.length > 0;
  const bannerBg = mode === 'dark'
    ? isCritical ? 'rgba(214, 50, 58, 0.14)' : 'rgba(240, 162, 2, 0.14)'
    : isCritical ? '#FDF2F2' : '#FFF9E6';
  const bannerBorder = mode === 'dark'
    ? isCritical ? '#D6323A' : '#F0A202'
    : isCritical ? '#F8B4B7' : '#FDE68A';
  const textColor = mode === 'dark'
    ? isCritical ? '#FF6B72' : '#FFC24B'
    : isCritical ? '#991B1B' : '#92400E';

  const issueItems = [];
  if (offline.length > 0) {
    issueItems.push(`${offline.length} Offline (${offline.map((g) => g.name || 'Unnamed').join(', ')})`);
  }
  if (degraded.length > 0) {
    issueItems.push(`${degraded.length} Degraded (${degraded.map((g) => g.name || 'Unnamed').join(', ')})`);
  }
  if (dropping.length > 0) {
    issueItems.push(`${dropping.length} Dropping Events`);
  }

  return (
    <Paper
      variant="outlined"
      data-testid="unhealthy-summary-banner"
      sx={{
        p: 2,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: bannerBg,
        borderColor: bannerBorder,
      }}
    >
      {isCritical ? (
        <ErrorOutlineIcon sx={{ color: textColor, fontSize: 26 }} />
      ) : (
        <WarningAmberIcon sx={{ color: textColor, fontSize: 26 }} />
      )}
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textColor }}>
          {isCritical ? 'Attention Required: Offline Gateway Detected' : 'Gateway Health Alert'}
        </Typography>
        <Typography variant="body2" sx={{ color: textColor, mt: 0.5 }}>
          {issueItems.join(' • ')}
        </Typography>
      </Box>
    </Paper>
  );
}
